import type { ErrorEvent } from '@sentry/react';

// Deliberately lean: only drop noise that is *universally* safe to discard and
// would never be an actionable bug. Everything else (network/timeout, chunk
// loads, server errors, etc.) is kept and managed in Sentry — via server-side
// Inbound Filters and per-issue mute — so we never silently lose a real failure
// at the source. The /sentry-analyze command does the smarter triage on top.
const NOISE_MESSAGE_PATTERNS = [
  // Cancelled/aborted requests — expected on route changes and debounced calls.
  'AbortError',
  'The user aborted a request',

  // Benign ResizeObserver chatter emitted by some browsers (not a real error).
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
];

const EXTENSION_FRAME_PATTERNS = [
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  'safari-web-extension://',
];

function getPrimaryException(event: ErrorEvent) {
  return event.exception?.values?.[0];
}

function isExtensionNoise(event: ErrorEvent): boolean {
  const frames = getPrimaryException(event)?.stacktrace?.frames ?? [];

  if (frames.length === 0) {
    return false;
  }

  // Only treat as extension noise when *every* frame with a filename comes
  // from an extension — a real app frame anywhere means it's worth keeping.
  const framesWithFile = frames.filter(frame => Boolean(frame.filename));

  return (
    framesWithFile.length > 0 &&
    framesWithFile.every(frame =>
      EXTENSION_FRAME_PATTERNS.some(pattern => frame.filename?.includes(pattern)),
    )
  );
}

/**
 * Returns true when a Sentry event should be dropped before it is sent.
 * Pure function so the noise rules can be unit-tested without the SDK.
 */
export function shouldDropEvent(event: ErrorEvent): boolean {
  const exception = getPrimaryException(event);
  const message = exception?.value ?? event.message ?? '';
  const type = exception?.type ?? '';

  if (NOISE_MESSAGE_PATTERNS.some(pattern => message.includes(pattern) || type.includes(pattern))) {
    return true;
  }

  return isExtensionNoise(event);
}
